import * as React from "react";
import { isApex } from "../../../../services/utils";
import { Alert, Card } from "antd";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { formSet, formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { store } from "../../../../store";
import { cloneDeep, get } from "lodash";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpRadioCardGroup } from "../../../gvc/testNewForm/CpRadioCardGroup";
import { CpRadioGroup } from "../../../gvc/testNewForm/CpRadioGroup";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { useCanRenderAdvanced } from "./useCanRenderAdvanced";
import { AcceptAllDedicatedLBCheck } from "./dlb_check";
import { CpErrorWarningArrayIcon } from "../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

interface Props {}
export const General: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const canRender = useCanRenderAdvanced();

  const name = useCpFormValue("advanced.name");
  const isDomainApex = React.useMemo(() => {
    return isApex(name);
  }, [name]);

  const dnsMode = useCpFormValue("advanced.spec.dnsMode");
  const routingMode = useCpFormValue("advanced.spec.routingMode");

  React.useEffect(() => {
    if (routingMode === "workloadSubdomain" || dnsMode === "ns") {
      formSetValue(id, "advanced.spec.certChallengeType", "dns01");

      const form = store.getState().form.instances[id];
      const onBlurCounter = form.meta.onBlurCounter;
      formSet(id, "meta.onBlurCounter", onBlurCounter + 1);
    }
  }, [dnsMode, routingMode]);

  React.useEffect(() => {
    if (routingMode === "gvcSubdomain") {
      const form = store.getState().form.instances[id];
      const values = form.values;

      const ports = get(values, `advanced.spec.ports`);
      const _ports = cloneDeep(ports);
      const clearedPorts = _ports.map((port) => ({ ...port, routes: [] }));

      formSetValue(id, "advanced.spec.ports", clearedPorts);

      const onBlurCounter = form.meta.onBlurCounter;
      formSet(id, "meta.onBlurCounter", onBlurCounter + 1);
    }
  }, [routingMode]);

  if (!canRender) {
    return null;
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container" data-section-id={"general"}>
      <Card>
        <CardTitle>
          Options
          <CpErrorWarningArrayIcon
            names={[
              `advanced.spec.dnsMode`,
              `advanced.spec.certChallengeType`,
              `advanced.spec.routingMode`,
              `advanced.spec.acceptAll`,
            ]}
          />
        </CardTitle>
        <CardContent>
          <div style={{ maxWidth: 1000 }}>
            <CpFormItem>
              <CpLabel name={`advanced.spec.dnsMode`}>DNS Mode</CpLabel>
              {isDomainApex ? (
                <Alert
                  showIcon
                  type={"info"}
                  className="w-fit mb-1"
                  message="Apex Domain can only use DNS Mode CNAME"
                />
              ) : null}
              <CpRadioCardGroup
                name={`advanced.spec.dnsMode`}
                grid
                options={[
                  {
                    label: "CNAME",
                    value: "cname",
                    boldLabel: true,
                    children: (
                      <ul className="mt-4">
                        <li className="list-disc text-sm ml-6">A CNAME record is configured in DNS for each domain</li>
                        <li className="list-disc text-sm ml-6">Best choice when Path Based Routing is required</li>
                        <li className="list-disc text-sm ml-6">Maintain full control over DNS</li>
                        <li className="list-disc text-sm ml-6">CDN/WAF Compatible</li>
                      </ul>
                    ),
                  },
                  {
                    label: "NS",
                    value: "ns",
                    boldLabel: true,
                    disabled: isDomainApex,
                    children: (
                      <ul className="mt-4">
                        <li className="list-disc text-sm ml-6">NS Records are used to delegate DNS to Control Plane</li>
                        <li className="list-disc text-sm ml-6">
                          Best choice when a unique DNS subdomain is required for each workload (GVC Subdomain Routing)
                        </li>
                        <li className="list-disc text-sm ml-6">One-time Configuration</li>
                        <li className="list-disc text-sm ml-6">Works for all current and future workloads in a GVC</li>
                      </ul>
                    ),
                  },
                ]}
              />
              <CpErrorWarning name="advanced.spec.dnsMode" />
            </CpFormItem>
          </div>
          <div style={{ maxWidth: 1000 }}>
          <CpFormItem>
            <CpLabel name={`advanced.spec.certChallengeType`}>Cert Challenge Type</CpLabel>
            {dnsMode === "ns" ? (
              <Alert
                showIcon
                type={"info"}
                className="w-fit mb-1"
                message="DNS Mode NS can only use Cert Challenge Type DNS01"
              />
            ) : routingMode === "workloadSubdomain" ? (
              <Alert
                showIcon
                type={"info"}
                className="w-fit mb-1"
                message="Workload Subdomain Routing must use Cert Challenge Type DNS01"
              />
            ) : null}
            <CpRadioCardGroup
              name={`advanced.spec.certChallengeType`}
              vertical
              options={[
                {
                  label: "http01",
                  value: "http01",
                  disabled: routingMode === "workloadSubdomain" || dnsMode === "ns",
                  boldLabel: true,
                  children: (
                    <div className="text-sm mt-2 color-label">
                      Uses HTTP-01 challenge and generates a standard certificate for just the domain name (not supported
                      for NS mode)
                    </div>
                  ),
                },
                {
                  label: "dns01",
                  value: "dns01",
                  boldLabel: true,
                  children: (
                    <div className="text-sm mt-2 color-label">
                      Uses DNS-01 challenge and generates a wildcard certificate
                    </div>
                  ),
                },
                {
                  label: "Unset",
                  value: "",
                  disabled: routingMode === "workloadSubdomain" || dnsMode === "ns",
                  boldLabel: true,
                  children: (
                    <div className="text-sm mt-2 color-label">
                      Defaults to wildcard certificate for NS domains, standard certificate for CNAME domains
                    </div>
                  ),
                },
              ]}
            />
            <CpErrorWarning name="advanced.spec.certChallengeType" />
          </CpFormItem>
          </div>
          <div style={{ maxWidth: 1000 }}>
            <CpFormItem>
              <CpLabel name={`advanced.spec.routingMode`}>Routing Mode</CpLabel>
              <CpRadioCardGroup
                name={`advanced.spec.routingMode`}
                grid
                options={[
                  {
                    label: "Path Based Routing",
                    value: "pathBased",
                    boldLabel: true,
                    children: (
                      <>
                        <div className="text-sm mb-2 mt-4 ml-2">
                          One or more paths, each mapped to a specific port on a specific workload in the GVC.
                        </div>
                        <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
                        <ul>
                          <li className="text-sm ml-4">https://{name}/</li>
                          <li className="text-sm ml-4">https://{name}/workload_one</li>
                          <li className="text-sm ml-4">https://{name}/workload_two</li>
                          <li className="text-sm ml-4">
                            https://{name}/{`<any>`}
                          </li>
                        </ul>
                      </>
                    ),
                  },
                  {
                    label: "GVC Subdomain Routing",
                    value: "gvcSubdomain",
                    boldLabel: true,
                    children: (
                      <>
                        <div className="text-sm mb-2 mt-4 ml-2">
                          A subdomain for each workload in the GVC, each automatically setup to route traffic to the
                          first port found for the workload.
                        </div>
                        <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
                        <ul>
                          <li className="text-sm ml-4">https://workload_one.{name}</li>
                          <li className="text-sm ml-4">https://workload_two.{name}</li>
                        </ul>
                      </>
                    ),
                  },
                  {
                    label: "Workload Subdomain Replica Routing",
                    value: "workloadSubdomain",
                    boldLabel: true,
                    children: (
                      <>
                        <div className="text-sm mb-2 mt-4 ml-2">
                          A subdomain for each replica of a stateful workload.
                        </div>
                        <div className="text-sm mb-2 mt-4 ml-2">Naming Format</div>
                        <ul>
                          <li className="font-mono text-sm ml-4">$workloadName-$replicaNumber-$location.$domainName</li>
                        </ul>
                        <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
                        <ul>
                          <li className="text-sm ml-4">https://workload_one-1-aws-eu-central-1.{name}</li>
                          <li className="text-sm ml-4">https://workload_one-2-aws-eu-central-1.{name}</li>
                        </ul>
                      </>
                    ),
                  },
                  {
                    label: "None",
                    value: "none",
                    boldLabel: true,
                    children: (
                      <>
                        <div className="mt-4 text-sm ml-2">No routes in ports.</div>
                      </>
                    ),
                  },
                ]}
              />
              <CpErrorWarning name="advanced.spec.routingMode" />
            </CpFormItem>
          </div>
          {routingMode === "none" ? null : (
            <>
              <CpFormItem>
                <CpLabel name={`advanced.spec.acceptAll`}>Wildcard</CpLabel>
                <CpDescription>
                  Accept all hosts is a full wildcard and will accept any arbitrary host. Accept all subdomains will
                  accept any host that is a sub domain of the domain so *.$DOMAIN
                </CpDescription>
                <CpRadioGroup
                  name={`advanced.spec.acceptAll`}
                  options={[
                    { label: "Accept All Hosts", value: "hosts" },
                    { label: "Accept All Subdomains", value: "subdomains" },
                    { label: "None", value: "none" },
                  ]}
                />
                <CpErrorWarning name="advanced.spec.acceptAll" />
              </CpFormItem>
              <AcceptAllDedicatedLBCheck />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
